import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

// Total idle time (ms) before the session expires
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

// How long before expiry to show the warning dialog
const WARNING_BEFORE_MS = 2 * 60 * 1000;

// User interactions that reset the idle timer
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

const LogoutTimer = ({ onLogout, onSessionExtend }) => {
    const [warningOpen, setWarningOpen] = useState(false);
    const [countdown, setCountdown] = useState(Math.floor(WARNING_BEFORE_MS / 1000));

    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const isWarningShownRef = useRef(false);

    // Keep stable refs to the latest callbacks so timers never capture stale closures
    const onLogoutRef = useRef(onLogout);
    const onSessionExtendRef = useRef(onSessionExtend);
    useEffect(() => { onLogoutRef.current = onLogout; }, [onLogout]);
    useEffect(() => { onSessionExtendRef.current = onSessionExtend; }, [onSessionExtend]);

    const clearAllTimers = useCallback(() => {
        clearTimeout(warningTimerRef.current);
        clearTimeout(logoutTimerRef.current);
        clearInterval(countdownIntervalRef.current);
    }, []);

    /**
     * (Re)starts the idle timer chain:
     *   1. After (IDLE_TIMEOUT_MS - WARNING_BEFORE_MS) of inactivity → open warning dialog
     *      with a live countdown.
     *   2. After WARNING_BEFORE_MS more seconds of inactivity → auto-logout.
     */
    const startIdleTimer = useCallback(() => {
        clearAllTimers();
        isWarningShownRef.current = false;

        warningTimerRef.current = setTimeout(() => {
            isWarningShownRef.current = true;
            let remaining = Math.floor(WARNING_BEFORE_MS / 1000);
            setCountdown(remaining);
            setWarningOpen(true);

            countdownIntervalRef.current = setInterval(() => {
                remaining -= 1;
                setCountdown(remaining);
                if (remaining <= 0) clearInterval(countdownIntervalRef.current);
            }, 1000);

            logoutTimerRef.current = setTimeout(() => {
                clearInterval(countdownIntervalRef.current);
                setWarningOpen(false);
                isWarningShownRef.current = false;
                onLogoutRef.current();
            }, WARNING_BEFORE_MS);
        }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);
    }, [clearAllTimers]);

    // Attach activity listeners once; reset idle timer on any interaction
    useEffect(() => {
        startIdleTimer();

        const onActivity = () => {
            // Don't reset once the warning is already showing — let the user decide
            if (!isWarningShownRef.current) {
                startIdleTimer();
            }
        };

        ACTIVITY_EVENTS.forEach(event =>
            window.addEventListener(event, onActivity, { passive: true })
        );

        return () => {
            clearAllTimers();
            ACTIVITY_EVENTS.forEach(event =>
                window.removeEventListener(event, onActivity)
            );
        };
    }, [startIdleTimer, clearAllTimers]);

    const handleStayLoggedIn = useCallback(() => {
        clearAllTimers();
        setWarningOpen(false);
        isWarningShownRef.current = false;
        onSessionExtendRef.current();
        startIdleTimer();
    }, [clearAllTimers, startIdleTimer]);

    const handleLogoutNow = useCallback(() => {
        clearAllTimers();
        setWarningOpen(false);
        isWarningShownRef.current = false;
        onLogoutRef.current();
    }, [clearAllTimers]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    return (
        <Dialog
            open={warningOpen}
            maxWidth="xs"
            fullWidth
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                },
            }}
        >
            {/* Top accent bar */}
            <MDBox sx={({ palette: { info } }) => ({ height: 4, background: `linear-gradient(90deg, ${info.main}, ${info.focus})` })} />

            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 2.5, pb: 1 }}>
                <AccessTimeOutlinedIcon sx={{ color: 'warning.main', fontSize: '1.4rem' }} />
                <MDTypography variant="h6" fontWeight="medium">
                    Session Timeout
                </MDTypography>
            </DialogTitle>

            <Divider sx={{ mb: 0 }} />

            <DialogContent sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
                {/* Countdown ring */}
                <MDBox
                    sx={{
                        mx: 'auto',
                        mb: 2.5,
                        width: 88,
                        height: 88,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #fff8e1, #fff3e0)',
                        border: '2.5px solid #ffa726',
                        boxShadow: '0 2px 12px rgba(255,167,38,0.25)',
                    }}
                >
                    <MDTypography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ color: '#e65100', letterSpacing: 2, fontVariantNumeric: 'tabular-nums' }}
                    >
                        {formatTime(countdown)}
                    </MDTypography>
                </MDBox>

                <MDTypography variant="body2" color="text" fontWeight="medium">
                    Your session has been idle.
                </MDTypography>
                <MDTypography variant="body2" color="text" mt={0.5}>
                    You will be logged out automatically when the timer reaches zero.
                </MDTypography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, gap: 1 }}>
                <MDButton variant="outlined" color="secondary" onClick={handleLogoutNow} fullWidth>
                    Log Out
                </MDButton>
                <MDButton variant="gradient" color="info" onClick={handleStayLoggedIn} fullWidth>
                    Stay Logged In
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

LogoutTimer.propTypes = {
    onLogout: PropTypes.func.isRequired,
    onSessionExtend: PropTypes.func.isRequired,
};

export default LogoutTimer;
